import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_DELAY = 10000;

export interface UseAutoDismissResult {
	isVisible: boolean;
	show: () => void;
	showWithoutTimer: () => void;
	hide: () => void;
	cancelTimer: () => void;
	restartTimer: () => void;
}

export const useAutoDismiss = (
	config: { delay?: number; onAutoDismiss?: () => void } = {},
): UseAutoDismissResult => {
	const delay = config.delay ?? DEFAULT_DELAY;
	const [isVisible, setIsVisible] = useState(false);
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const onAutoDismissRef = useRef(config.onAutoDismiss);

	onAutoDismissRef.current = config.onAutoDismiss;

	const clearTimer = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = undefined;
		}
	}, []);

	const startTimer = useCallback(() => {
		clearTimer();
		timeoutRef.current = setTimeout(() => {
			setIsVisible(false);
			onAutoDismissRef.current?.();
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = undefined;
			}
		}, delay);
	}, [clearTimer, delay]);

	const show = useCallback(() => {
		setIsVisible(true);
		startTimer();
	}, [startTimer]);

	const showWithoutTimer = useCallback(() => {
		clearTimer();
		setIsVisible(true);
	}, [clearTimer]);

	const hide = useCallback(() => {
		clearTimer();
		setIsVisible(false);
	}, [clearTimer]);

	useEffect(() => {
		return () => {
			if (timeoutRef.current && onAutoDismissRef.current) {
				onAutoDismissRef.current();
			}
			clearTimer();
		};
	}, [clearTimer]);

	return {
		isVisible,
		show,
		showWithoutTimer,
		hide,
		cancelTimer: clearTimer,
		restartTimer: startTimer,
	};
};
